import { useEffect, useState } from "react";
import { RequestError, useApi } from "../../../services";
import { isAxiosError } from "axios";
import { ErrorModal } from "../../modals";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Skeleton,
  Box,
  Checkbox,
} from "@mui/material";
import { PropsMapLayerStyle } from "../../edit/mapa/mapItem";
import { PaintTypes, StyleButton } from "../../mapa";
import { SelectedItem } from ".";
import { generateRandomColor } from "../../../core";
import { useParams } from "react-router-dom";

export interface ListShapeProps {
  files: { name: string; layers: string[] | null; }[];
}

interface Props {
  startLayers: SelectedItem[];
  onChange?: (layers: SelectedItem[], styles: PropsMapLayerStyle[]) => void;
  layersStyles?: PropsMapLayerStyle[];
}
export const ListDamShapes: React.FC<Props> = (data) => {
  const { layersStyles, onChange, startLayers } = data;

  const api = useApi();
  const { company, barragem } = useParams();

  const [shapes, setShapes] = useState<SelectedItem[] | RequestError>(startLayers);
  const [styles, setStyles] = useState<PropsMapLayerStyle[]>(layersStyles ?? []);

  useEffect(() => {
    const getShapes = async () => {
      if (!company || !barragem) return;
      if ("error" in shapes) return;
      const data = await api.damFiles.listShapes(parseInt(barragem));
      if ("error" in data) {
        setShapes(data);
        return;
      }

      // Layers
      const files: SelectedItem[] = data.files.flatMap((shapeNow) => {
        return (shapeNow.layers ?? [undefined]).map((layer) => {
          const selected = shapes.filter(
            (shape) => shape.selected
          ).some((shape) => (shape.name === shapeNow.name && shape.layer === layer));

          return {
            name: shapeNow.name,
            selected: selected,
            layer: layer,
          };
        });
      });
      setShapes(files);
    };

    getShapes();
  }, [barragem]);

  const handleSelectAll = () => {
    if (!shapes || "error" in shapes) return;

    const all = shapes.every((shape) => shape.selected);

    const newShapes = shapes.map((shape) => {
      const newShape = { ...shape };
      newShape.selected = !all;
      return newShape;
    });
    setShapes(newShapes);
    if (onChange) onChange(newShapes, styles);
  };
  const handleSelect = (selected: boolean, file: string, layer?: string) => {
    if (!shapes || "error" in shapes) return;

    const newShapes = shapes.map((shape) => {
      const newShape = { ...shape };
      if (newShape.name === file && newShape.layer === layer) {
        newShape.selected = selected;
      }
      return newShape;
    });
    setShapes(newShapes);
    if (onChange) onChange(newShapes, styles);
  };
  const handleStyle = (style: PaintTypes, file: string, layer?: string) => {
    if (!shapes || "error" in shapes) return;

    const newStyles = styles.filter((styleNow) => !(styleNow.file === file && styleNow.layer === layer));
    newStyles.push({
      file: file,
      layer: layer,
      style: style,
    });
    setStyles(newStyles);
    if (onChange) onChange(shapes, newStyles);
  };

  // Erro
  if (shapes && "error" in shapes) return <ErrorModal {...shapes} />;

  if (!shapes) {
    return <Box sx={{
      height: "100%",
      flex: 1,
    }}>
      <Skeleton />
    </Box>;
  }

  return <TableContainer component={Paper}>
    < Table stickyHeader sx={{ width: "100%" }
    }>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={!shapes.every((shape) => !shape.selected) && shapes.some((shape) => !shape.selected)}
              checked={shapes.every((shape) => shape.selected)}
              onChange={handleSelectAll}
              inputProps={{
                'aria-label': 'select all desserts',
              }}
            />
          </TableCell>
          <TableCell align="center">Shape</TableCell>
          <TableCell align="center">Layer</TableCell>
          <TableCell align="center">Style</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {shapes.map((layer, i) => {
          const style = styles.filter((style) => style.file == layer.name && style.layer == layer.layer);
          const styleShow = (style.length > 0) ? style[0].style : {
            fillColor: generateRandomColor(),
            color: "black",
          };

          return <TableRow
            key={`${i}`}
            hover
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            {/* CheckBox */}
            <TableCell padding="checkbox" align="center">
              <Checkbox
                color="primary"
                value={layer.selected}
                checked={layer.selected}
                onChange={(event, checked) => handleSelect(checked, layer.name, layer.layer)}
              />
            </TableCell>

            {/* File */}
            <TableCell
              align="center"
              component="th"
              scope="row"
              onClick={() => handleSelect(!layer.selected, layer.name, layer.layer)}
            >
              {layer.name}
            </TableCell>

            {/* Layer */}
            <TableCell
              align="center"
              onClick={() => handleSelect(!layer.selected, layer.name, layer.layer)}
            >
              {layer.layer ?? "-"}
            </TableCell>

            {/* Estilo */}
            <TableCell align="center">
              {
                <StyleButton
                  onChange={(style) => handleStyle(style, layer.name, layer.layer)}
                  initialStyle={styleShow}
                />
              }
            </TableCell>
          </TableRow>;
        })}
      </TableBody>
    </Table >
  </TableContainer >;
};
